import { alpha, ThemeOptions, createTheme } from '@mui/material/styles';

export const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#222f39',
      dark: '#182127',
      light: '#415665',
      contrastText: '#f8fbfc',
    },
    secondary: {
      main: '#4fa3d9',
      light: '#e7f3fb',
      dark: '#2c6c9d',
      contrastText: '#0f2c3f',
    },
    background: {
      default: '#f3f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2a33',
      secondary: '#5f6f7c',
    },
    divider: alpha('#222f39', 0.1),
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '4.6rem',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontSize: '0.95rem',
      fontWeight: 600,
    },
    body2: {
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    fontSize: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: '#f3f6f8',
        },
        body: {
          backgroundColor: '#f3f6f8',
          color: '#1f2a33',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 10px 30px rgba(34, 47, 57, 0.06)',
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 10px 24px rgba(24, 33, 39, 0.18)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 36,
          borderRadius: theme.shape.borderRadius,
          paddingInline: theme.spacing(1.5),
        }),
        containedPrimary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontSize: '0.825rem',
          fontWeight: 600,
          lineHeight: 1.4,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: theme.spacing(0.75),
          marginLeft: 0,
          marginRight: 0,
          fontSize: '0.75rem',
          lineHeight: 1.4,
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create(['border-color', 'box-shadow']),
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.35),
          },
          '&.Mui-focused': {
            boxShadow: `${alpha(theme.palette.primary.main, 0.14)} 0 0 0 4px`,
          },
        }),
        input: ({ theme }) => ({
          padding: theme.spacing(1.1, 1.4),
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          minHeight: 'unset',
          paddingTop: theme.spacing(1.1),
          paddingBottom: theme.spacing(1.1),
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: 0,
          marginRight: theme.spacing(2),
          alignItems: 'center',
        }),
        label: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: '0.875rem',
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          height: 3,
          borderRadius: 999,
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 40,
          paddingInline: theme.spacing(1.5),
          textTransform: 'none',
          fontWeight: 600,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRight: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          marginTop: theme.spacing(1),
          borderRadius: theme.shape.borderRadius + 2,
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          marginInline: theme.spacing(1),
          width: `calc(100% - ${theme.spacing(2)})`,
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(97, 97, 97, 0.9)',
          color: 'white',
          fontSize: '12px',
          border: 'none',
        },
        arrow: {
          color: 'rgba(97, 97, 97, 0.9)',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
